import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { endpoints, baseUrl } from '../../api/api';
import { AdminUserRecord, UserListSummary, UserFilter } from './types';
import AdminHeader from './components/AdminHeader';
import UsersTable from './components/UsersTable';

const FILTER_LABEL: Record<UserFilter, string> = {
  all:                'All Users',
  new_7d:             'New (last 7 days)',
  new_30d:            'New (last 30 days)',
  no_books:           'No Books Yet',
  incomplete_profile: 'Incomplete Profile',
  declined_terms:     'Declined T&C',
};

interface SummaryCardProps {
  label: string;
  value: number | string;
  sub?: string;
  filterValue: UserFilter;
  activeFilter: UserFilter;
  onSelect: (f: UserFilter) => void;
  accentColor?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  label, value, sub, filterValue, activeFilter, onSelect, accentColor = 'border-gray-800',
}) => {
  const isActive = activeFilter === filterValue;
  return (
    <button
      onClick={() => onSelect(filterValue === activeFilter ? 'all' : filterValue)}
      className={`text-left bg-white rounded-lg shadow px-5 py-4 transition-all w-full
        ${isActive ? `ring-2 ring-offset-1 ring-gray-800 border-l-4 ${accentColor}` : 'hover:shadow-md border-l-4 border-transparent'}`}
    >
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${isActive ? 'text-gray-900' : 'text-gray-800'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
      <p className={`mt-1.5 text-xs font-medium ${isActive ? 'text-gray-700' : 'text-gray-300'}`}>
        {isActive ? '✓ Filtering' : 'Click to filter'}
      </p>
    </button>
  );
};

const UserDirectory: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers]             = useState<AdminUserRecord[]>([]);
  const [summary, setSummary]         = useState<UserListSummary | null>(null);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState<UserFilter>('all');
  const [search, setSearch]           = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalCount, setTotalCount]   = useState(0);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', '20');
      if (filter !== 'all') params.set('filter', filter);
      if (search.trim()) params.set('search', search.trim());

      const response = await axios.get(
        `${baseUrl}${endpoints.adminGetUsers}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUsers(response.data.users || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.total || 0);
      if (response.data.summary) setSummary(response.data.summary);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, search, currentPage]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.role !== 'admin') { navigate('/'); return; }
      } catch { navigate('/'); return; }
    }
    fetchUsers();
  }, [fetchUsers, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleCardFilter = (f: UserFilter) => {
    setFilter(f);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setCurrentPage(1);
  };

  const handleViewBooks = (userId: string, userName: string) => {
    navigate(`/admin?userId=${userId}&userName=${encodeURIComponent(userName)}`);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader onLogout={handleLogout} />

      <main className="px-4 py-8 space-y-6">
        {/* Summary strip — each card is a filter */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <SummaryCard label="Total Users"        value={summary.total}             filterValue="all"                activeFilter={filter} onSelect={handleCardFilter} accentColor="border-gray-800" />
            <SummaryCard label="New (last 7 days)"  value={summary.newLast7Days}      filterValue="new_7d"             activeFilter={filter} onSelect={handleCardFilter} accentColor="border-orange-400" sub="signups" />
            <SummaryCard label="New (last 30 days)" value={summary.newLast30Days}     filterValue="new_30d"            activeFilter={filter} onSelect={handleCardFilter} accentColor="border-blue-400"   sub="signups" />
            <SummaryCard label="No Books Yet"       value={summary.noBooks}           filterValue="no_books"           activeFilter={filter} onSelect={handleCardFilter} accentColor="border-amber-400"  sub="never created" />
            <SummaryCard label="Profile Incomplete" value={summary.profileIncomplete} filterValue="incomplete_profile" activeFilter={filter} onSelect={handleCardFilter} accentColor="border-red-400"    sub="need follow-up" />
            <SummaryCard label="Declined T&C"       value={summary.declinedTerms}     filterValue="declined_terms"     activeFilter={filter} onSelect={handleCardFilter} accentColor="border-purple-400" sub="leads only" />
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <h2 className="text-lg font-semibold text-gray-900 shrink-0">
              Users ({loading ? '…' : totalCount})
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              {filter !== 'all' && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                  {FILTER_LABEL[filter]}
                  <button
                    onClick={() => { setFilter('all'); setCurrentPage(1); }}
                    className="text-gray-400 hover:text-gray-700"
                    title="Clear filter"
                  >
                    ×
                  </button>
                </span>
              )}
              <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search name, email, phone…"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md w-56 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  className="px-3 py-2 text-sm bg-gray-800 text-white rounded-md hover:bg-gray-700"
                >
                  Search
                </button>
                {search && (
                  <button
                    type="button"
                    onClick={() => { setSearch(''); setSearchInput(''); setCurrentPage(1); }}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Clear
                  </button>
                )}
              </form>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" />
            </div>
          ) : (
            <UsersTable users={users} onViewBooks={handleViewBooks} />
          )}

          {!loading && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserDirectory;
